import React, { useEffect, useRef, useState } from 'react';
import './AnimatedBackground.css';

interface AnimatedBackgroundProps {
  className?: string;
  scrollProgress?: number;
}

const AnimatedBackground: React.FC<AnimatedBackgroundProps> = ({ className = '', scrollProgress = 0 }) => {
  const interactiveRef = useRef<HTMLDivElement>(null);
  const [forceRender, setForceRender] = useState(0);

  useEffect(() => {
    const interactive = interactiveRef.current;
    if (!interactive) return;

    let curX = 0;
    let curY = 0;
    let tgX = 0;
    let tgY = 0;

    const move = () => {
      curX += (tgX - curX) / 20;
      curY += (tgY - curY) / 20;
      if (interactive) {
        interactive.style.transform = `translate(${Math.round(curX)}px, ${Math.round(curY)}px)`;
      }
      requestAnimationFrame(move);
    };

    const handleMouseMove = (event: MouseEvent) => {
      tgX = event.clientX;
      tgY = event.clientY;
    };

    window.addEventListener('mousemove', handleMouseMove);
    move();

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Force re-render when theme changes
  useEffect(() => {
    const handleThemeChange = (event: any) => {
      console.log('🎨 AnimatedBackground: Received themeChanged event', event.detail);
      console.log('🎨 AnimatedBackground: Current CSS variables:');
      const root = document.documentElement;
      console.log('  --color-bg1:', getComputedStyle(root).getPropertyValue('--color-bg1'));
      console.log('  --color1:', getComputedStyle(root).getPropertyValue('--color1'));
      console.log('  --color2:', getComputedStyle(root).getPropertyValue('--color2'));
      
      // Force a re-render by updating state
      setForceRender(prev => {
        console.log('🎨 AnimatedBackground: Force render from', prev, 'to', prev + 1);
        return prev + 1;
      });
    };

    // Listen for custom theme change event
    console.log('🎨 AnimatedBackground: Setting up themeChanged event listener');
    window.addEventListener('themeChanged', handleThemeChange);

    return () => {
      console.log('🎨 AnimatedBackground: Cleaning up themeChanged event listener');
      window.removeEventListener('themeChanged', handleThemeChange);
    };
  }, []);

  // Calculate opacity for the moving globes based on scroll progress
  // Keep globes fully visible in hero section, start fading only after scrolling
  const globesOpacity = Math.max(0, 1 - Math.max(0, scrollProgress - 0.3) * 2);

  console.log('🎨 AnimatedBackground: Rendering with forceRender =', forceRender, 'scrollProgress =', scrollProgress, 'globesOpacity =', globesOpacity);

  return (
    <div key={forceRender} className={`animated-background ${className}`}>
      <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
        <defs>
          <filter id="goo">
            <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur" />
            <feColorMatrix 
              in="blur" 
              mode="matrix" 
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -8" 
              result="goo" 
            />
            <feBlend in="SourceGraphic" in2="goo" />
          </filter>
        </defs>
      </svg>
      
      {/* Moving globes */}
      <div 
        className="gradients-container"
        style={{ 
          opacity: globesOpacity,
          transition: 'opacity 0.8s ease-out'
        }}
      >
        <div className="g1"></div>
        <div className="g2"></div>
        <div className="g3"></div>
        <div className="g4"></div>
        <div className="g5"></div>
        <div className="interactive" ref={interactiveRef}></div>
      </div>
    </div>
  );
};

export default AnimatedBackground;
